import * as R from "ramda";

import { supabase } from "../../../data/supabase/client";
import { logError } from "../../../utils/error";
import { getTimeRangeFilter, TimeRange } from "../../../utils/timerange";
import { generateTrends } from "../utils/trend";

interface EnrichedScorecard {
  interaction_id: string;
  call_summary: string;
  user_email: string;
  created: string;
  overall_score: number;
  attributes: Record<string, number>;
}

interface ProtocolScorecardMetricsRow {
  interaction_id: string;
  user_email: string | null;
  created: string;
  call_summary: string | null;
  overall_score: number;
  attributes: Record<string, number>;
}

export interface ProtocolMetrics {
  averageScore: number;
  maxScore: number;
  minScore: number;
  totalCalls: number;
  enrichedScorecards: EnrichedScorecard[];
  topCalls: {
    calls: EnrichedScorecard[];
    trends: Record<string, number[][]>;
  };
  bottomCalls: {
    calls: EnrichedScorecard[];
    trends: Record<string, number[][]>;
  };
  recentCalls: {
    calls: EnrichedScorecard[];
    trends: Record<string, number[][]>;
  };
  randomCalls: {
    calls: EnrichedScorecard[];
    trends: Record<string, number[][]>;
  };
}

interface FetchProtocolMetricsParams {
  timeRange: TimeRange;
  timezone: string;
  protocol: string;
  user: string;
  permittedEmails: string[] | null;
}

export const fetchProtocolMetrics = async (
  params: FetchProtocolMetricsParams
): Promise<ProtocolMetrics> => {
  const { timeRange, timezone, protocol, user, permittedEmails } = params;

  try {
    const { startDate, endDate } = getTimeRangeFilter(timeRange, timezone);

    const { data, error } = await supabase.rpc("get_protocol_scorecard_metrics", {
      p_lens_name: protocol,
      p_start_date: startDate,
      p_end_date: endDate,
      p_user_email: user !== "all" ? user.toLowerCase() : undefined,
      p_permitted_emails: permittedEmails && permittedEmails.length > 0
        ? permittedEmails.map(email => email.toLowerCase())
        : undefined
    });

    if (error) {
      throw error;
    }
    if (!data || data.length === 0) {
      throw new Error("No data found for the selected protocol");
    }

    const enrichedScorecards: EnrichedScorecard[] = (data as ProtocolScorecardMetricsRow[]).map((row) => ({
      interaction_id: row.interaction_id,
      call_summary: row.call_summary ?? "",
      user_email: row.user_email ?? "",
      created: row.created,
      overall_score: Number(row.overall_score) || 0,
      attributes: row.attributes
    }));

    const totalCalls = enrichedScorecards.length;
    const overallScores = enrichedScorecards.map(item => item.overall_score);
    const averageScore = R.mean(overallScores);
    const maxScore = Math.max(...overallScores);
    const minScore = Math.min(...overallScores);

    // Sort by overall score to get top and bottom calls
    const sortedByScore = R.sortBy(R.prop("overall_score"), enrichedScorecards);
    const bottomCalls = R.take(5, sortedByScore);
    const topCalls = R.takeLast(5, sortedByScore);

    // Get recent 20 calls (already sorted by date)
    const recentCalls = R.takeLast(20, enrichedScorecards);

    // Get random 20 calls
    const randomCalls = pickRandomUnique(enrichedScorecards, 20);

    // Return complete metrics
    return {
      averageScore,
      maxScore,
      minScore,
      totalCalls,
      enrichedScorecards,
      topCalls: {
        calls: topCalls,
        trends: generateTrends(topCalls)
      },
      bottomCalls: {
        calls: bottomCalls,
        trends: generateTrends(bottomCalls)
      },
      recentCalls: {
        calls: recentCalls,
        trends: generateTrends(recentCalls)
      },
      randomCalls: {
        calls: randomCalls,
        trends: generateTrends(randomCalls)
      }
    };
  } catch (error) {
    logError("Error fetching protocol metrics", error);
    throw error;
  }
};

/**
 * Picks N random unique items from an array
 * @param items The source array to pick from
 * @param count The number of items to pick
 * @returns A new array containing N random unique items
 */
export const pickRandomUnique = <T, >(items: readonly T[], count: number): T[] => {
  if (count < 0) {
    return [];
  }

  // Ensure we don't try to pick more items than available
  const safeCount = Math.min(count, items.length);

  // Create array of indices and shuffle them
  const indices = Array.from({ length: items.length }, (_, i) => i);

  // Fisher-Yates shuffle algorithm
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }

  // Take first N indices and map them back to items
  return indices
    .slice(0, safeCount)
    .map(index => items[index]);
};
